import type { FieldErrors, FieldValues, Resolver } from 'react-hook-form'
import type { z } from 'zod'

/**
 * Minimal Zod resolver for React Hook Form, matching the auth workstream's
 * pattern. Issue messages are catalog keys so both locales share one schema.
 */
export function zodResolver<T extends FieldValues>(
  schema: z.ZodType<T>,
): Resolver<T> {
  return async (values) => {
    const result = schema.safeParse(values)
    if (result.success) {
      return { values: result.data, errors: {} }
    }

    const errors: Record<string, unknown> = {}
    for (const issue of result.error.issues) {
      const path = issue.path.map((part) => String(part))
      const key = path.length > 0 ? path : ['root']
      let cursor: Record<string, unknown> = errors
      for (const segment of key.slice(0, -1)) {
        const next = cursor[segment]
        if (typeof next !== 'object' || next === null) {
          cursor[segment] = {}
        }
        cursor = cursor[segment] as Record<string, unknown>
      }
      const leaf = key[key.length - 1] ?? 'root'
      if (!cursor[leaf]) {
        cursor[leaf] = { type: issue.code, message: issue.message }
      }
    }

    return { values: {}, errors: errors as FieldErrors<T> }
  }
}
